import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import useInitializeMap from './hooks/useInitializeMap';
import useMapManager from './hooks/useMapManager';
import type { LocationMarker } from '@fhnw/types/location';

export type MapState = {
  map: mapboxgl.Map | null;
  activeLocation: LocationMarker | undefined;
  isSetFitBounds: boolean;
  isClient: boolean;
  isFullscreen: boolean;
};

export type MapAction =
  | {
      type: 'SET_MAP_AND_IS_CLIENT';
      payload: { map: mapboxgl.Map; isClient: boolean };
    }
  | { type: 'SET_FIT_BOUNDS'; payload: boolean }
  | {
      type: 'SET_LOCATION_AND_BOUNDS';
      payload: { location: LocationMarker; fitBounds: boolean };
    }
  | { type: 'SET_FULLSCREEN'; payload: boolean };

function mapReducer(state: MapState, action: MapAction): MapState {
  switch (action.type) {
    case 'SET_MAP_AND_IS_CLIENT':
      return {
        ...state,
        map: action.payload.map,
        isClient: action.payload.isClient,
      };
    case 'SET_FIT_BOUNDS':
      return { ...state, isSetFitBounds: action.payload };
    case 'SET_LOCATION_AND_BOUNDS':
      return {
        ...state,
        activeLocation: action.payload.location,
        isSetFitBounds: action.payload.fitBounds,
      };
    case 'SET_FULLSCREEN':
      return { ...state, isFullscreen: action.payload };
    default:
      return state;
  }
}

interface LocationMapProps {
  locations: LocationMarker[];
  activeLocation?: LocationMarker;
  onChange?: (location?: LocationMarker) => void;
  className?: string;
}

const LocationMapContent: React.FC<LocationMapProps> = ({
  locations,
  className,
  activeLocation,
  onChange,
}) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [currentLocation, setCurrentLocation] = useState<
    LocationMarker | undefined
  >(activeLocation || locations[0]);

  const initialState = useMemo(
    () => ({
      map: null as mapboxgl.Map | null,
      activeLocation: activeLocation || locations[0],
      isSetFitBounds: false,
      isClient: false,
      isFullscreen: false,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeLocation, locations[0]],
  );

  const [state, dispatch] = useReducer(mapReducer, initialState);

  const { initialize } = useInitializeMap({
    mapContainerRef,
    state,
    dispatch,
  });

  const handleLocationChange = (location: LocationMarker | undefined) => {
    setCurrentLocation(location);
    if (onChange) {
      onChange(location);
    }
  };

  const { handleLocationClick } = useMapManager({
    locationsData: locations,
    state,
    dispatch,
    onChange: handleLocationChange,
    mapInstance: state.map as mapboxgl.Map,
  });

  useEffect(() => {
    if (
      activeLocation &&
      activeLocation.id !== state.activeLocation?.id &&
      activeLocation.id !== currentLocation?.id
    ) {
      handleLocationClick(activeLocation);
    }
  }, [
    activeLocation,
    state.activeLocation?.id,
    currentLocation?.id,
    handleLocationClick,
  ]);

  useEffect(() => {
    const href = '/static/css/mapbox-gl.css';

    const link = document.createElement('link');
    link.href = href;
    link.rel = 'preload';
    link.as = 'style';
    link.onload = () => {
      link.rel = 'stylesheet';
    };

    document.head.appendChild(link);

    const init = () => {
      requestAnimationFrame(() => {
        initialize();
      });
    };

    window.addEventListener('load', init);

    return () => {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }

      if (window) {
        window.removeEventListener('load', init);
      }
    };
  }, [initialize]);

  return (
    locations &&
    locations.length > 0 && (
      <div className={'location-map-container ' + className}>
        <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
      </div>
    )
  );
};

export default LocationMapContent;
